import excel from "exceljs";
import {
  importPartV1,
  type ImportPartV1Body,
  type ImportChildPartV1Body,
} from "@/api/importPartService";
import {
  fetchMaterialCodesBySupplier,
  fetchPartSKPBySupplier,
  fetchBreakdownProcessV1,
} from "./cachedQueries";
import { getCellStr, getCellNum, findInsertRow } from "./sheetHelpers";
import type {
  ImportedChildPartV1,
  ImportedBreakdownV1,
  ImportedV1ProcessRow,
  ImportWarning,
  ParsedImportV1Result,
} from "./types";

// ─── Header parsing ──────────────────────────────────────────────────────────

const EMPTY_SENTINELS = new Set(["none", "null", "undefined", "n/a", "-", "na"]);
const normalizeCell = (val: string): string =>
  EMPTY_SENTINELS.has(val.trim().toLowerCase()) ? "" : val.trim();

const parseHeaderV1 = (sheet: excel.Worksheet) => {
  let partNo = "",
    ac = "",
    rt = "";

  const normalizeLabel = (raw: string) =>
    raw.replace(/[\s/:.]/g, "").toLowerCase();

  sheet.eachRow((row, rowNum) => {
    if (rowNum > 11) return;
    row.eachCell((cell, colNum) => {
      const label = normalizeLabel(cell.value?.toString().trim() ?? "");
      if (label === "partno") partNo = normalizeCell(getCellStr(sheet, rowNum, colNum + 1));
      if (label === "ac") ac = normalizeCell(getCellStr(sheet, rowNum, colNum + 1));
      if (label === "rt") rt = normalizeCell(getCellStr(sheet, rowNum, colNum + 1));
    });
  });

  return { partNo, ac, rt: rt.replace(/^0+(?=\d)/, "") };
};

// ─── Dynamic column detection ─────────────────────────────────────────────────

type ProcessGroup = { colIndex: number; name: string }[];

const PREFIXES = {
  secondTier: "2nd Tier Process- ",
  other: "Other Process- ",
  oem: "OEM Process- ",
  spare: "S/P Process- ",
} as const;

// Normalize legacy typo "Procress" → "Process" for OEM columns
const normalizeOemLabel = (label: string) =>
  label.replace(/OEM Procress/g, "OEM Process");

const detectProcessColumns = (
  sheet: excel.Worksheet,
  headerRow: number,
): {
  secondTier: ProcessGroup;
  other: ProcessGroup;
  oem: ProcessGroup;
  spare: ProcessGroup;
} => {
  const groups = {
    secondTier: [] as ProcessGroup,
    other: [] as ProcessGroup,
    oem: [] as ProcessGroup,
    spare: [] as ProcessGroup,
  };

  // Dynamic process columns start from col 33 onward
  const row = sheet.getRow(headerRow);
  row.eachCell({ includeEmpty: false }, (cell, colNum) => {
    if (colNum < 33) return;
    const label = normalizeOemLabel(cell.value?.toString().trim() ?? "");
    if (label.startsWith(PREFIXES.secondTier))
      groups.secondTier.push({
        colIndex: colNum,
        name: label.slice(PREFIXES.secondTier.length),
      });
    else if (label.startsWith(PREFIXES.other))
      groups.other.push({
        colIndex: colNum,
        name: label.slice(PREFIXES.other.length),
      });
    else if (label.startsWith(PREFIXES.oem))
      groups.oem.push({
        colIndex: colNum,
        name: label.slice(PREFIXES.oem.length),
      });
    else if (label.startsWith(PREFIXES.spare))
      groups.spare.push({
        colIndex: colNum,
        name: label.slice(PREFIXES.spare.length),
      });
  });

  return groups;
};

// ─── Column offset map ────────────────────────────────────────────────────────

type ColMap = {
  secondTierTotal: number;
  secondTierProcessCols: number[];
  secondTierComment: number;
  otherTotal: number;
  otherProcessCols: number[];
  otherComment: number;
  percent: number;
  otherPercent: number;
  oemTotal: number;
  oemProcessCols: number[];
  oemComment: number;
  oemAdmin: number;
  oemProfit: number;
  spareTotal: number;
  spareProcessCols: number[];
  spareComment: number;
  spareAdmin: number;
  spareProfit: number;
  cpOemAdmin: number;
  cpOemProfit: number;
  cpSpareAdmin: number;
  cpSpareProfit: number;
};

const buildColMap = (
  secondTierCols: number[],
  otherCols: number[],
  oemCols: number[],
  spareCols: number[],
): ColMap => {
  const N1 = secondTierCols.length;
  const N2 = otherCols.length;
  const N3 = oemCols.length;
  const N4 = spareCols.length;
  const S = N1 + N2 + N3;
  
  return {
    secondTierTotal: 32,
    secondTierProcessCols: secondTierCols,
    secondTierComment: 33 + N1,
    otherTotal: 34 + N1,
    otherProcessCols: otherCols,
    otherComment: 35 + N1 + N2,
    percent: 36 + N1 + N2,
    otherPercent: 37 + N1 + N2,
    oemTotal: 38 + N1 + N2,
    oemProcessCols: oemCols,
    oemComment: 39 + S,
    oemAdmin: 40 + S,
    oemProfit: 41 + S,
    spareTotal: 42 + S,
    spareProcessCols: spareCols,
    spareComment: 43 + S + N4,
    spareAdmin: 44 + S + N4,
    spareProfit: 45 + S + N4,
    cpOemAdmin: 47 + S + N4,
    cpOemProfit: 48 + S + N4,
    cpSpareAdmin: 50 + S + N4,
    cpSpareProfit: 51 + S + N4,
  };
};

// ─── Name → id resolution ─────────────────────────────────────────────────────

const buildNameMap = (list: { id: number; name: string }[] | undefined) =>
  new Map((list ?? []).map((item) => [item.name.toLowerCase(), item.id]));

const findId = (map: Map<string, number>, name: string) =>
  map.get(name.toLowerCase()) ?? 0;

// ─── Parse child parts ────────────────────────────────────────────────────────

const parseChildPartsV1 = async (
  sheet: excel.Worksheet,
  insertRow: number,
  colMap: ColMap,
  processGroups: ReturnType<typeof detectProcessColumns>,
  supplierCode: string,
): Promise<{
  child_parts: ImportedChildPartV1[];
  materialIdMap: Map<string, number>;
  warnings: ImportWarning[];
}> => {
  const child_parts: ImportedChildPartV1[] = [];
  const materialIdMap = new Map<string, number>();
  const warnings: ImportWarning[] = [];

  const allMaterialCodes = await fetchMaterialCodesBySupplier(supplierCode);
  const materialCodeMap = new Map(
    (allMaterialCodes ?? []).map((mc) => [mc.material_code, mc]),
  );

  for (let r = insertRow; ; r++) {
    const childPartNo = getCellStr(sheet, r, 1);
    if (!childPartNo) break; // end of table

    const materialCode = getCellStr(sheet, r, 19);
    const matchedMaterial = materialCodeMap.get(materialCode);
    const materialId = matchedMaterial?.material_id ?? 0;
    materialIdMap.set(childPartNo, materialId);
    const materialPrice = matchedMaterial?.material_price ?? 0;

    if (!matchedMaterial) {
      warnings.push({
        type: "material-not-found",
        childPartNo,
        message: `Material code "${materialCode}" ไม่พบสำหรับ child part "${childPartNo}"`,
      });
    }

    // parse dynamic process columns
    const secondTierProcesses: ImportedV1ProcessRow[] =
      processGroups.secondTier.map((g) => ({
        name: g.name,
        cost: getCellNum(sheet, r, g.colIndex),
      }));
    const otherProcesses: ImportedV1ProcessRow[] = processGroups.other.map(
      (g) => ({ name: g.name, cost: getCellNum(sheet, r, g.colIndex) }),
    );
    const oemProcesses: ImportedV1ProcessRow[] = processGroups.oem.map((g) => ({
      name: g.name,
      cost: getCellNum(sheet, r, g.colIndex),
    }));
    const spareProcesses: ImportedV1ProcessRow[] = processGroups.spare.map(
      (g) => ({ name: g.name, cost: getCellNum(sheet, r, g.colIndex) }),
    );

    const isPurchasedRaw = getCellStr(sheet, r, 28).trim();
    const isPurchased =
      isPurchasedRaw === "✓" || isPurchasedRaw.toLowerCase() === "yes";

    child_parts.push({
      child_part_no: childPartNo,
      child_part_name: getCellStr(sheet, r, 2),
      qty: getCellNum(sheet, r, 5),
      material_code: materialCode,
      material_id: materialId,
      material_price: materialPrice,
      amount: getCellNum(sheet, r, 21),
      ratio: getCellNum(sheet, r, 22),
      consumption: getCellNum(sheet, r, 23),
      part_weight: getCellNum(sheet, r, 24),
      scrap_weight: getCellNum(sheet, r, 25),
      is_purchased: isPurchased,
      material_cost: isPurchased
        ? getCellNum(sheet, r, 27)
        : materialPrice * getCellNum(sheet, r, 23),
      scrap_price: getCellNum(sheet, r, 29),
      admin: getCellNum(sheet, r, 30),
      profit: getCellNum(sheet, r, 31),
      second_tier_processes: secondTierProcesses,
      second_tier_process_comment: getCellStr(
        sheet,
        r,
        colMap.secondTierComment,
      ),
      other_processes: otherProcesses,
      other_process_comment: getCellStr(sheet, r, colMap.otherComment),
      percent: getCellNum(sheet, r, colMap.percent),
      other_percent: getCellNum(sheet, r, colMap.otherPercent),
      oem_processes: oemProcesses,
      oem_process_comment: getCellStr(sheet, r, colMap.oemComment),
      oem_process_admin: getCellNum(sheet, r, colMap.oemAdmin),
      oem_process_profit: getCellNum(sheet, r, colMap.oemProfit),
      spare_processes: spareProcesses,
      spare_process_comment: getCellStr(sheet, r, colMap.spareComment),
      spare_process_admin: getCellNum(sheet, r, colMap.spareAdmin),
      spare_process_profit: getCellNum(sheet, r, colMap.spareProfit),
      cp_oem_admin: getCellNum(sheet, r, colMap.cpOemAdmin),
      cp_oem_profit: getCellNum(sheet, r, colMap.cpOemProfit),
      cp_spare_admin: getCellNum(sheet, r, colMap.cpSpareAdmin),
      cp_spare_profit: getCellNum(sheet, r, colMap.cpSpareProfit),
    });
  }

  return { child_parts, materialIdMap, warnings };
};

// ─── Build ImportPartV1Body ────────────────────────────────────────────────────

const buildImportV1Body = (
  preview: ImportedBreakdownV1,
  resolvedPartId: number,
  materialIdMap: Map<string, number>,
  nameMaps: {
    secondTierMap: Map<string, number>;
    otherMap: Map<string, number>;
    oemMap: Map<string, number>;
    spareMap: Map<string, number>;
  },
  isMigration: boolean,
  isActive: boolean,
): ImportPartV1Body => {
  const { secondTierMap, otherMap, oemMap, spareMap } = nameMaps;

  return {
    part: {
      part_id: resolvedPartId,
      part_number: preview.header.part_no,
      part_name: preview.header.part_name,
      ac: preview.header.ac,
      rt: preview.header.rt,
    },
    child_part: preview.child_parts.map(
      (cp): ImportChildPartV1Body => ({
        no: cp.child_part_no,
        name: cp.child_part_name,
        qty: cp.qty,
        material_id: materialIdMap.get(cp.child_part_no) ?? 0,
        material_price: cp.material_price,
        amount: cp.amount,
        ratio: cp.ratio,
        consumption: cp.consumption,
        part_weight: cp.part_weight,
        scrap_weight: cp.scrap_weight,
        is_purchased: cp.is_purchased,
        material_cost: cp.material_cost,
        scrap_price: cp.scrap_price,
        admin: cp.admin,
        profit: cp.profit,
        second_tier_process_comment: cp.second_tier_process_comment,
        second_tier_processes: cp.second_tier_processes.map((p) => ({
          ms_second_tier_process_v1_id: findId(secondTierMap, p.name),
          cost: p.cost,
        })),
        other_process_comment: cp.other_process_comment,
        other_processes: cp.other_processes.map((p) => ({
          ms_other_process_v1_id: findId(otherMap, p.name),
          cost: p.cost,
        })),
        percent: cp.percent,
        other_percent: cp.other_percent,
        oem_process_comment: cp.oem_process_comment,
        oem_process_admin: cp.oem_process_admin,
        oem_process_profit: cp.oem_process_profit,
        oem_processes: cp.oem_processes.map((p) => ({
          ms_oem_process_v1_id: findId(oemMap, p.name),
          cost: p.cost,
        })),
        spare_process_comment: cp.spare_process_comment,
        spare_process_admin: cp.spare_process_admin,
        spare_process_profit: cp.spare_process_profit,
        spare_processes: cp.spare_processes.map((p) => ({
          ms_spare_process_v1_id: findId(spareMap, p.name),
          cost: p.cost,
        })),
        cp_oem_admin: cp.cp_oem_admin,
        cp_oem_profit: cp.cp_oem_profit,
        cp_spare_admin: cp.cp_spare_admin,
        cp_spare_profit: cp.cp_spare_profit,
      }),
    ),
    is_migration: isMigration,
    is_active: isActive,
  };
};

// ─── Parse function ───────────────────────────────────────────────────────────

export const parsePartV1FromExcel = async (
  file: File,
  supplierCode: string,
  isMigration = false,
  isActive = true,
): Promise<ParsedImportV1Result> => {
  const warnings: ImportWarning[] = [];

  const workbook = new excel.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const sheet = workbook.getWorksheet("CBD");
  if (!sheet) throw new Error("Sheet 'CBD' not found");

  // ── Header ─────────────────────────────────────────────────────────────────
  const header = parseHeaderV1(sheet);

  const partSKPList = await fetchPartSKPBySupplier(supplierCode);
  const matchedPart = partSKPList.find(
    (p) =>
      p.part_number === header.partNo &&
      p.ac === header.ac &&
      p.rt === header.rt,
  );
  if (!matchedPart && header.ac) {
    warnings.push({
      type: "part-not-found",
      message: `ไม่พบ Part: part_no="${header.partNo}" ac="${header.ac}" rt="${header.rt}"`,
    });
  }
  const resolvedPartId = matchedPart?.part_id ?? 0;
  const partName = matchedPart?.part_name ?? "";

  // ── Locate data rows ────────────────────────────────────────────────────────
  const insertRow = findInsertRow(sheet, "CBD:", 2);
  if (insertRow === -1)
    throw new Error("ไม่พบ 'CBD:' header ใน sheet — ตรวจสอบ template");

  const headerRow = insertRow - 1;
  const processGroups = detectProcessColumns(sheet, headerRow);

  const colMap = buildColMap(
    processGroups.secondTier.map((g) => g.colIndex),
    processGroups.other.map((g) => g.colIndex),
    processGroups.oem.map((g) => g.colIndex),
    processGroups.spare.map((g) => g.colIndex),
  );

  // ── Child parts ─────────────────────────────────────────────────────────────
  const {
    child_parts,
    materialIdMap,
    warnings: cpWarnings,
  } = await parseChildPartsV1(
    sheet,
    insertRow,
    colMap,
    processGroups,
    supplierCode,
  );
  warnings.push(...cpWarnings);

  // ── Master process → name map for id resolution ────────────────────────────
  const breakdownProcess = await fetchBreakdownProcessV1(supplierCode);
  const nameMaps = {
    secondTierMap: buildNameMap(breakdownProcess?.second_tier_process),
    otherMap: buildNameMap(breakdownProcess?.other_process),
    oemMap: buildNameMap(breakdownProcess?.oem_process),
    spareMap: buildNameMap(breakdownProcess?.spare_process),
  };

  // Warn for process names not in master
  const collectProcessWarnings = (
    names: string[],
    map: Map<string, number>,
    label: string,
  ) => {
    const seen = new Set<string>();
    for (const name of names) {
      const key = name.toLowerCase();
      if (!map.get(key) && !seen.has(key)) {
        seen.add(key);
        warnings.push({
          type: "material-cost-process-not-found",
          processName: name,
          message: `${label} "${name}" ไม่พบใน Master Data`,
        });
      }
    }
  };

  collectProcessWarnings(
    child_parts.flatMap((cp) => cp.second_tier_processes.map((p) => p.name)),
    nameMaps.secondTierMap,
    "2nd Tier Process",
  );
  collectProcessWarnings(
    child_parts.flatMap((cp) => cp.other_processes.map((p) => p.name)),
    nameMaps.otherMap,
    "Other Process",
  );
  collectProcessWarnings(
    child_parts.flatMap((cp) => cp.oem_processes.map((p) => p.name)),
    nameMaps.oemMap,
    "OEM Process",
  );
  collectProcessWarnings(
    child_parts.flatMap((cp) => cp.spare_processes.map((p) => p.name)),
    nameMaps.spareMap,
    "S/P Process",
  );

  const preview: ImportedBreakdownV1 = {
    header: {
      part_no: header.partNo,
      part_name: partName,
      ac: header.ac,
      rt: header.rt,
    },
    child_parts,
  };

  const body = buildImportV1Body(
    preview,
    resolvedPartId,
    materialIdMap,
    nameMaps,
    isMigration,
    isActive,
  );

  return { preview, body, warnings, supplierCode };
};

// ─── Submit ───────────────────────────────────────────────────────────────────

export const submitImportPartV1 = async (
  body: ImportPartV1Body,
  supplierCode: string,
): Promise<void> => {
  await importPartV1(body, supplierCode);
};
